import { useState, useCallback, useRef, useEffect } from 'react';
import { Form } from 'antd';
import {
  postCatalogRating,
  CatalogRatingWithComment,
  getCatalogRatingsByCatalogId,
  CatalogRating,
} from '../../apis/catalog-ratings';

interface UseCatalogRatingProps {
  serviceId: string;
  serviceName: string;
}

interface FormValues {
  comment: string;
}

const COMMENT_RULES = {
  maxLength: 500,
  minLength: 15,
};

const LOW_RATING_THRESHOLD = 3;

const ANTI_SPAM_CONFIG = {
  cooldownMs: 2000,
  warningThreshold: 3,
  warningMessage: 'Aguarde um momento antes de votar novamente',
};

const getCurrentUserId = (): string => {
  let userId = localStorage.getItem('xvia_user_id');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('xvia_user_id', userId);
  }
  return userId;
};

const isUserRating = (rating: CatalogRating): boolean => {
  const userId = getCurrentUserId();
  const userRatings = JSON.parse(
    localStorage.getItem(`xvia_user_ratings_${userId}`) || '{}'
  );
  return userRatings[rating.id || ''] === true;
};

const markAsUserRating = (ratingId: string): void => {
  const userId = getCurrentUserId();
  const userRatings = JSON.parse(
    localStorage.getItem(`xvia_user_ratings_${userId}`) || '{}'
  );
  userRatings[ratingId] = true;
  localStorage.setItem(
    `xvia_user_ratings_${userId}`,
    JSON.stringify(userRatings)
  );
};

export const useCatalogRating = ({ serviceId }: UseCatalogRatingProps) => {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const [commentLength, setCommentLength] = useState<number>(0);
  const [userRating, setUserRating] = useState<CatalogRating | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const [isInCooldown, setIsInCooldown] = useState(false);
  const clickCountRef = useRef(0);
  const lastClickTimeRef = useRef(0);
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const loadUserRating = async () => {
      if (!serviceId) return;

      setIsLoading(true);
      try {
        const ratings = await getCatalogRatingsByCatalogId(serviceId);

        const userRatingFound = ratings.find(isUserRating);

        if (userRatingFound) {
          setUserRating(userRatingFound);
          setRating(userRatingFound.value);
        }
      } catch (error) {
        console.error('Erro ao carregar avaliações:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserRating();
  }, [serviceId]);

  const resetAntiSpamState = useCallback(() => {
    clickCountRef.current = 0;
    lastClickTimeRef.current = 0;
    if (cooldownTimeoutRef.current) {
      clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    }
  }, []);

  const checkSpamProtection = useCallback(
    (currentTime: number) => {
      if (isInCooldown) {
        return false;
      }

      if (
        currentTime - lastClickTimeRef.current >
        ANTI_SPAM_CONFIG.cooldownMs
      ) {
        clickCountRef.current = 0;
      }

      clickCountRef.current++;
      lastClickTimeRef.current = currentTime;

      if (clickCountRef.current >= ANTI_SPAM_CONFIG.warningThreshold) {
        setIsInCooldown(true);
        setFeedback({
          message: ANTI_SPAM_CONFIG.warningMessage,
          type: 'info',
        });

        cooldownTimeoutRef.current = setTimeout(() => {
          setIsInCooldown(false);
          resetAntiSpamState();
        }, ANTI_SPAM_CONFIG.cooldownMs);

        return false;
      }

      return true;
    },
    [isInCooldown, resetAntiSpamState]
  );

  const handleSubmitRating = useCallback(
    async (selectedRating: number, comment?: string) => {
      if (!serviceId) {
        throw new Error('ID do serviço não encontrado');
      }

      setIsSubmitting(true);
      try {
        const ratingData: CatalogRatingWithComment = {
          value: selectedRating,
          ...(comment && { comment }),
        };

        const response = await postCatalogRating(serviceId, ratingData);

        const newRating: CatalogRating = {
          id: response.id,
          value: selectedRating,
          catalogItem: {} as any,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        markAsUserRating(response.id);

        setUserRating(newRating);
        setRating(selectedRating);

        setFeedback(null);
        setHoverRating(0);
        form.resetFields();
        setCommentLength(0);
        resetAntiSpamState();
      } catch (error) {
        console.error(error);
        setFeedback({
          message: 'Erro ao enviar avaliação. Tente novamente.',
          type: 'error',
        });
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [serviceId, form, resetAntiSpamState]
  );

  const handleStarClick = useCallback(
    (selectedRating: number) => {
      if (userRating) {
        return;
      }

      setRating(selectedRating);
      setHoverRating(0);
      setFeedback(null);
    },
    [userRating]
  );

  const handleStarMouseEnter = useCallback(
    (starRating: number) => {
      if (!userRating) {
        setHoverRating(starRating);
      }
    },
    [userRating]
  );

  const handleStarMouseLeave = useCallback(() => {
    setHoverRating(0);
  }, []);

  const handleStarKeyDown = useCallback(
    (starRating: number, evt: React.KeyboardEvent<SVGSVGElement>) => {
      if (evt.key !== 'Enter' && evt.key !== ' ') {
        return;
      }
      evt.preventDefault();

      if (userRating) {
        return;
      }

      setRating(starRating);
      setHoverRating(0);
      setFeedback(null);
    },
    [userRating]
  );

  const handleModalSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      await handleSubmitRating(rating, values.comment);
      setShowModal(false);
    } catch {
      return;
    }
  }, [form, handleSubmitRating, rating]);

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
    if (!userRating) {
      setRating(0);
    }
    setHoverRating(0);
    form.resetFields();
    setCommentLength(0);
    resetAntiSpamState();
    setFeedback(null);
  }, [form, resetAntiSpamState, userRating]);

  const handleCommentChange = useCallback((value: string) => {
    setCommentLength(value?.length || 0);
  }, []);

  const handleSubmitButtonClick = useCallback(async () => {
    const currentTime = Date.now();

    if (!checkSpamProtection(currentTime)) {
      return 'cooldown';
    }

    if (userRating) {
      return 'already-rated';
    }

    if (!rating) {
      setFeedback({
        message: 'Dê uma nota para esse serviço antes',
        type: 'error',
      });
      return 'missing-rating';
    }

    if (rating <= LOW_RATING_THRESHOLD) {
      setFeedback(null);
      setShowModal(true);
      return 'modal';
    }

    try {
      await handleSubmitRating(rating);
      return 'success';
    } catch {
      return 'error';
    }
  }, [rating, userRating, handleSubmitRating, checkSpamProtection]);

  const currentRating = userRating ? userRating.value : hoverRating || rating;

  return {
    rating,
    hoverRating,
    showModal,
    isSubmitting,
    form,
    commentLength,
    currentRating,
    isInCooldown,
    userRating,
    isLoading,
    feedback,

    handleStarClick,
    handleStarMouseEnter,
    handleStarMouseLeave,
    handleStarKeyDown,
    handleSubmitButtonClick,
    handleModalSubmit,
    handleModalCancel,
    handleCommentChange,

    COMMENT_RULES,
  };
};
